import { useEffect, useId, useMemo, useRef, useState } from "react";
import styles from "./ImportBpasDialog.module.css";

type Edition = {
  id: string;
  editionId: string;
  editionName: string | null;
};

type Publication = {
  name: string;
  editions: Edition[];
};

type ImportBpasDialogProps = {
  targetEditionName: string;
  publications: Publication[];
  selectedEditionConfigId: string | null;
  onSelectEdition: (id: string) => void;
  isImporting: boolean;
  onImport: () => void;
  onCancel: () => void;
};

export function ImportBpasDialog({
  targetEditionName,
  publications,
  selectedEditionConfigId,
  onSelectEdition,
  isImporting,
  onImport,
  onCancel,
}: ImportBpasDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const initialPub = useMemo(() => {
    if (!selectedEditionConfigId) return publications[0]?.name ?? null;
    for (const pub of publications) {
      if (pub.editions.some(e => e.id === selectedEditionConfigId)) return pub.name;
    }
    return publications[0]?.name ?? null;
  }, [publications, selectedEditionConfigId]);

  const [selectedPub, setSelectedPub] = useState<string | null>(initialPub);

  const visibleEditions = useMemo(
    () => publications.find(p => p.name === selectedPub)?.editions ?? [],
    [publications, selectedPub],
  );

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !isImporting) onCancel();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel, isImporting]);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <div className={styles.overlay} onClick={isImporting ? undefined : onCancel}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h3 id={titleId} className={styles.title}>
          Import BPAs from previous edition
        </h3>
        <p className={styles.description}>
          Select a source edition to copy practice areas and ranking table structure into{" "}
          <strong>{targetEditionName || "this edition"}</strong>. Researcher assignments will not be
          copied.
        </p>

        <div className={styles.columns}>
          <div className={styles.column}>
            <div className={styles.columnHeader}>Publication</div>
            <ul className={styles.columnList}>
              {publications.map(pub => (
                <li key={pub.name}>
                  <button
                    type="button"
                    className={`${styles.columnItem} ${selectedPub === pub.name ? styles.columnItemSelected : ""}`}
                    onClick={() => setSelectedPub(pub.name)}
                  >
                    <span className={styles.columnItemLabel}>{pub.name}</span>
                    <span className={styles.columnItemCount}>{pub.editions.length}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.column}>
            <div className={styles.columnHeader}>Edition</div>
            <ul className={styles.columnList}>
              {visibleEditions.map(edition => (
                <li key={edition.id}>
                  <button
                    type="button"
                    className={`${styles.columnItem} ${selectedEditionConfigId === edition.id ? styles.columnItemSelected : ""}`}
                    onClick={() => onSelectEdition(edition.id)}
                  >
                    {edition.editionName ?? edition.id}
                  </button>
                </li>
              ))}
              {visibleEditions.length === 0 && <li className={styles.columnEmpty}>No editions</li>}
            </ul>
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={isImporting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.importButton}
            onClick={onImport}
            disabled={isImporting || !selectedEditionConfigId}
          >
            {isImporting ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}
